// Custom scroll restoration logic for Gatsby
// Applies scroll position restoration on navigation

import React from "react";
import { getLenisInstance } from "./src/components/utils/smoothScroll";
import PageTransition from "./src/components/utils/pageTransition";

// Import scroll position utilities only in browser environment
let saveScrollPosition, restoreScrollPosition, isBackNavigation;
if (typeof window !== 'undefined') {
  const scrollPositionUtils = require('./src/components/utils/scrollPosition');
  saveScrollPosition = scrollPositionUtils.saveScrollPosition;
  restoreScrollPosition = scrollPositionUtils.restoreScrollPosition;
  isBackNavigation = scrollPositionUtils.isBackNavigation;
}

// Called before a page is about to change
export const onPreRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window === 'undefined') return;
  
  // Set a flag to indicate we're in the middle of a navigation
  window.__isNavigating = true;
  
  // Prevent default scroll behavior during navigation
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }
  
  // Save scroll position before navigating away
  if (prevLocation && typeof saveScrollPosition === 'function') {
    saveScrollPosition(prevLocation.pathname);
  }
  
  // Notify all distortion effects about the transition
  if (window.distortionEffects) {
    window.distortionEffects.forEach(effect => {
      if (effect && typeof effect.setTransitionMode === 'function') {
        effect.setTransitionMode(true);
      }
    });
  }
  
  // Dynamically import GSAP/ScrollTrigger to avoid SSR issues
  Promise.resolve().then(() => {
    try {
      // eslint-disable-next-line global-require
      const gsapModule = require("gsap");
      const gsap = gsapModule.default || gsapModule;
      // eslint-disable-next-line global-require
      const stModule = require("gsap/dist/ScrollTrigger");
      const ScrollTrigger = stModule.default || stModule;
      
      if (gsap && ScrollTrigger && typeof ScrollTrigger.getAll === "function") {
        // Kill all ScrollTriggers before navigation
        ScrollTrigger.getAll().forEach(t => t.kill());
      }
    } catch (error) {}
  });
};

// Called after a page has changed
export const onRouteUpdate = ({ location }) => {
  // Reset the navigation flag after a short delay
  setTimeout(() => {
    window.__isNavigating = false;
    
    // Notify all distortion effects that transition is complete
    if (window.distortionEffects) {
      setTimeout(() => {
        window.distortionEffects.forEach(effect => {
          if (effect && typeof effect.setTransitionMode === 'function') {
            effect.setTransitionMode(false);
          }
        });
      }, 100);
    }
  }, 100);
  
  // Restore scroll restoration after navigation
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }
  
  // Dynamically import GSAP/ScrollTrigger to avoid SSR issues
  Promise.resolve().then(() => {
    try {
      // eslint-disable-next-line global-require
      const gsapModule = require("gsap");
      const gsap = gsapModule.default || gsapModule;
      // eslint-disable-next-line global-require
      const stModule = require("gsap/dist/ScrollTrigger");
      const ScrollTrigger = stModule.default || stModule;
      
      if (gsap && ScrollTrigger) {
        // Register ScrollTrigger plugin
        gsap.registerPlugin(ScrollTrigger);
        
        // Use a sequence of refreshes to ensure everything is captured
        if (typeof ScrollTrigger.refresh === "function") {
          // Refresh with increasing delays
          [0, 100, 500, 1000].forEach(delay => {
            setTimeout(() => ScrollTrigger.refresh(), delay);
          });
          
          // Mark navigation as complete
          setTimeout(() => {
            window.__isNavigating = false;
          }, 1100);
        }
      }
      
      // Resize Lenis after navigation
      const lenis = getLenisInstance && getLenisInstance();
      if (lenis && typeof lenis.resize === "function") {
        setTimeout(() => lenis.resize(), 100);
      }
    } catch (error) {}
  });
};

const transitionDelay = 250;

export const shouldUpdateScroll = ({ prevRouterProps, routerProps }) => {
  if (!routerProps) return false;
  if (typeof window === 'undefined') return true;
  
  const currentPath = routerProps.location.pathname;
  const prevPath = prevRouterProps ? prevRouterProps.location.pathname : null;
  
  // Check if we're navigating to a work detail page (project page)
  const isWorkDetailPage = currentPath.includes('/work/');
  
  // Always scroll to top when navigating to a work detail page
  if (isWorkDetailPage) {
    // Use Lenis for smooth scrolling if available
    const lenis = getLenisInstance();
    if (lenis && typeof lenis.scrollTo === 'function') {
      setTimeout(() => lenis.scrollTo(0, { immediate: true }), transitionDelay);
    } else {
      setTimeout(() => window.scrollTo(0, 0), transitionDelay);
    }
    return false; // Prevent default scroll behavior
  }
  
  // Check if we're navigating back from a work detail page to the main page
  const isBackToMainFromWork = prevPath && prevPath.includes('/work/') && 
                              (currentPath === '/' || currentPath.endsWith('/'));
  
  // If we're navigating back from a work page, restore the scroll position
  if ((isBackToMainFromWork || (typeof isBackNavigation === 'function' && isBackNavigation())) && 
      typeof restoreScrollPosition === 'function') {
    setTimeout(() => {
      restoreScrollPosition(currentPath);
    }, transitionDelay);
    return false; // Prevent default scroll behavior
  }
  
  // For other navigations, scroll to top
  return true;
};

// Wrap the page with our PageTransition component
export const wrapPageElement = ({ element, props }) => {
  return <PageTransition {...props}>{element}</PageTransition>;
};

